<template>
	<RouterLink
		class="message-result"
		:data-message-id="result.message.id"
		:to="linkToChat"
	>
		<div class="message-result-inner">
			<div class="result-group-header">
				<div class="result-group" v-text="result.group.title" />
				<EyeSlashIcon
					type="solid"
					v-if="result.group.ishidden"
					class="hidden-group-icon"
				/>
			</div>
			<div class="result-content">
				<ProfilePic class="result-profile" :user="user" size="36px" />
				<div class="result-content-inner">
					<div class="result-header">
						<div class="result-name" v-text="user.fullname" />
						<MessageTime :date="result.message.created_at" />
					</div>

					<PollCard
						v-if="hasPoll"
						:pollId="result.message.poll_id"
						class="result-poll-card"
						:highlights="result.highlight"
					/>

					<MessageMedia v-if="media" :message="media" />

					<UnfurlCard
						v-if="result.message.resource"
						:resource="result.message.resource"
						:disabled="true"
					/>

					<div
						v-if="!hasPoll"
						class="result-wrapper"
						:class="{ translated: showTranslation }"
						data-private
					>
						<div
							class="result-text line-clamp-2"
							v-if="!scrubbedText"
							:class="{ 'emojis-only': this.isOnlyEmoji }"
							v-text="originalText"
						/>
						<div
							class="result-text"
							v-if="scrubbedText"
							:class="{ 'emojis-only': this.isOnlyEmoji }"
							v-safe-html="scrubbedText"
						/>
					</div>
				</div>
			</div>
		</div>
		<ChevronIcon class="chevron" right />

		<div v-if="isLoading" class="fetching-wrapper">
			<div class="search-fetching">
				<IosSpinner
					v-if="isLoading"
					background="rgba(255, 255, 255, 0.7)"
					spinnerColor="#4f5053"
					class="connecting-spinner"
				/>
			</div>
		</div>
	</RouterLink>
</template>

<script>
import { getMyUser } from '../../store'
import { ProfilePic } from '../common'
import MessageTime from '../Message/MessageTime'
import MessageMedia from '../Message/MessageMedia'
import { ChevronIcon, EyeSlashIcon, EarthIcon } from '@/icons'
import UnfurlCard from '@/components/Message/UnfurlCard'
import { scrubHighlight, getLinkToChatForMessage } from './searchUtils'
import { getUser } from '@/store'
import { containsOnlyEmoji } from '@/helpers/emojis'
import { parseMessageParts, partType } from '../Message/utils'
import { renderVueComponent } from '../../helperMethods'
import IosSpinner from '../IosSpinner'
import PollCard from '../Polls/PollCard'

export default {
	name: 'MessageResult',
	props: ['result', 'isLoading'],
	components: {
		ProfilePic,
		MessageTime,
		ChevronIcon,
		UnfurlCard,
		EyeSlashIcon,
		IosSpinner,
		MessageMedia,
		PollCard,
	},
	data() {
		return {
			displayedLang: null,
		}
	},

	computed: {
		scrubbedText() {
			const { language } = getMyUser()
			const { message, messagelangs } = this.result.highlight || {}
			const { lang } = this.result.message

			// eslint-disable-next-line vue/no-side-effects-in-computed-properties
			this.displayedLang = lang

			// STEP 1: If no highlight info at all, fall back to original message text
			if (!this.result.highlight) return null

			if (messagelangs && messagelangs[language]?.[0]) {
				// STEP 2: If there's a translation into our preferred language, prefer that.
				// eslint-disable-next-line vue/no-side-effects-in-computed-properties
				this.displayedLang = language
				return this.showTranslation
					? scrubHighlight(messagelangs[language][0]) +
							renderVueComponent(EarthIcon, { active: true }).$el.outerHTML
					: scrubHighlight(messagelangs[language][0])
			}
			if (messagelangs && messagelangs[lang]?.[0]) {
				// STEP 3: Otherwise, if there's highlight text specific to this message's language, use that.  The reason we prefer
				// the language specific version is that it comes from a language-specific index with features such as stemming.
				return scrubHighlight(messagelangs[lang][0])
			}
			if (message?.[0]) {
				// STEP 4: Otherwise, if we have hightlight text for the original message text, use that.
				return scrubHighlight(message[0])
			}
			// STEP 5: If all the above failed, then we'll fall through to displaying the original message text.
			return null
		},
		media() {
			const { message, highlight } = this.result
			if (!message.hasMedia) return null

			// Clone the message to avoid mutating the original
			const enhancedMessage = Object.assign({}, message)
			enhancedMessage.messagemedia = [...message.messagemedia]

			// Check for the new detailed media highlights format first
			if (highlight?.media?.length) {
				enhancedMessage.messagemedia = enhancedMessage.messagemedia.map(
					(mediaItem) => {
						const mediaHighlight = highlight.media.find(
							(h) => h.id === mediaItem.id
						)
						if (mediaHighlight?.title?.[0]) {
							return {
								...mediaItem,
								title: scrubHighlight(mediaHighlight.title[0]),
							}
						}
						return mediaItem
					}
				)
			}
			// Fall back to legacy format for backwards compatibility
			else if (highlight?.messagemedia?.title?.[0]) {
				// Apply to first media item (legacy behavior)
				if (enhancedMessage.messagemedia[0]) {
					enhancedMessage.messagemedia[0] = {
						...enhancedMessage.messagemedia[0],
						title: scrubHighlight(highlight.messagemedia.title[0]),
					}
				}
			}

			return enhancedMessage
		},
		hasMediaHighlight() {
			return this.result.highlight.messagemedia?.title.length > 0
		},
		user() {
			return getUser(this.result.message.user_id) || {}
		},
		showTranslation() {
			const { lang } = this.result.message
			return lang !== null && this.displayedLang !== lang
		},
		isOnlyEmoji() {
			return containsOnlyEmoji(this.scrubbedText || this.originalText, 100)
		},
		originalText() {
			return parseMessageParts(this.result.message)
				.map(({ type, userId, user, text }) => {
					switch (type) {
						case partType.MENTION:
							if (user) return `@${user.fullname}`
							if (userId === 'everyone') return '@everyone'
							return `User #${userId}`
						case partType.HYPERLINK:
						case partType.TEXT:
							return text
						default:
							return null
					}
				})
				.filter((part) => part)
				.join('')
		},
		linkToChat() {
			return getLinkToChatForMessage(this.result.message)
		},
		hasPoll() {
			return this.result.message.poll_id
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.message-result {
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 100%;
	padding: 12px;
	text-decoration: none;
	color: var(--neutral-800);
}
.result-group,
.result-text:not(.emojis-only) {
	font-family: Avenir-Roman;
	font-size: 17px;
	color: #4f5053;
}

.result-group-header {
	@include flex(12px);
	width: max-content;
	margin-bottom: 8px;
	.result-group {
		font-size: 14px;
	}
	.hidden-group-icon {
		font-size: 16px;
		color: var(--neutral-600);
	}
}

.result-content {
	display: flex;
	align-items: flex-start;
	.result-header {
		display: flex;
		align-items: center;
	}
}

.result-wrapper {
	margin-top: 2px;
}

.result-text {
	white-space: pre-wrap;
	width: 100%;
}

.result-profile {
	flex-shrink: 0;
	margin-right: 8px;
}

.result-name {
	font-family: Avenir-Heavy;
	font-size: 15px;
	color: var(--neutral-800);
}

:deep(.search-highlight) {
	background: rgba(#ffbb00, 0.18);
	font-style: normal;
}
:deep(.media-view-container) {
	max-width: none;
	margin: 10px 10px 0 0;
	&.fullname {
		.attachment-wrapper {
			height: auto;
		}
		.attachment-title {
			max-height: none;
		}
	}
	.media-image,
	.media-video {
		margin-top: 0;
		height: auto;
		width: auto;
		max-width: 200px;
		max-height: 200px;
	}
}
:deep(.message-time) {
	font-family: Avenir-Heavy;
}

.translated {
	background-color: rgba(73, 164, 218, 0.15);
	border-radius: 8px;
	padding-left: 4px;
	padding-right: 4px;
	margin-left: -4px;
	margin-right: -4px;
	width: max-content;
	max-width: calc(100vw - 425px);
	.result-text {
		:deep(.pronto-icon) {
			font-size: 14px;
		}
	}
}

.chevron {
	flex-shrink: 0;
}

.message-result-inner,
.result-content-inner {
	width: 100%;
}

.result-text :deep(svg) {
	vertical-align: middle;
	margin-top: -5px;
	margin-left: 6px;
	height: 14px;
	width: 14px;
	&.active {
		fill: var(--pronto-blue);
	}
	path.light {
		display: none;
	}
}

.fetching-wrapper {
	position: absolute;
	left: 0;
	right: 0;
	top: 0;
	bottom: 0;
	.search-fetching {
		height: 100%;
		display: flex;
		justify-content: center;
		align-items: center;
		background-color: rgba(255, 255, 255, 0.7);
		border-radius: 8px;
	}
}
</style>
